import React, { useState, useEffect } from "react";
import DraggablePanel from "./DraggablePanel";
import { getHistoryActions, getHistoryStore, makeHistorySnapshot } from "../../stores/historyStore";
import { getSceneState, getSceneActions } from "../../stores/sceneStore";
import { useFileStore } from "../../stores/fileStore";
import { logger } from "../../utils/logger";

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between px-3 py-2">
    <span className="text-gray-300 text-sm select-none">{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const HistoryPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [historySize, setHistorySize] = useState(0);
  const [undoSummary, setUndoSummary] = useState("");
  const [redoSummary, setRedoSummary] = useState("");
  const [historyList, setHistoryList] = useState([]);
  const [restoreError, setRestoreError] = useState(null);

  const historyActions = getHistoryActions();

  // Update state periodically instead of using hooks
  useEffect(() => {
    if (!open) return;

    const updateStatus = () => {
      try {
        // Use the history store directly to get state
        const historyStore = getHistoryStore();
        const state = historyStore.getState();

        const pastLength = state.past?.length || 0;
        const futureLength = state.future?.length || 0;

        const undoEntry = pastLength > 0 ? state.past[pastLength - 1] : null;
        const redoEntry = futureLength > 0 ? state.future[0] : null;

        setCanUndo(pastLength > 0 && undoEntry?.disabled !== true);
        setCanRedo(futureLength > 0 && redoEntry?.disabled !== true);
        setHistorySize(pastLength + futureLength);

        // Get summaries safely
        if (undoEntry?.summary) {
          setUndoSummary(undoEntry.summary);
        } else {
          setUndoSummary("");
        }

        if (redoEntry?.summary) {
          setRedoSummary(redoEntry.summary);
        } else {
          setRedoSummary("");
        }

        // Update history list with all past actions
        const pastHistory =
          state.past?.map((entry, index) => ({
            id: `past-${index}`,
            summary: entry.summary,
            type: "past",
            index,
            disabled: entry.disabled === true,
          })) || [];

        const futureHistory =
          state.future?.map((entry, index) => ({
            id: `future-${index}`,
            summary: entry.summary,
            type: "future",
            index,
            disabled: entry.disabled === true,
          })) || [];

        setHistoryList([...pastHistory, ...futureHistory]);
      } catch (error) {
        logger.warn("[HistoryPanel] Error updating status:", error);
        setCanUndo(false);
        setCanRedo(false);
        setHistorySize(0);
        setUndoSummary("");
        setRedoSummary("");
        setHistoryList([]);
      }
    };

    // Update immediately
    updateStatus();

    // Update every 500ms when panel is open
    const interval = setInterval(updateStatus, 500);

    return () => clearInterval(interval);
  }, [open, historyActions]);

  const handleUndo = () => {
    if (canUndo) {
      try {
        historyActions.undo(
          () => makeHistorySnapshot(getSceneState()),
          (snapshot) => {
            const robotActions = getSceneActions();
            robotActions.restoreState(snapshot.scene);
            useFileStore.setState({ keyframes: snapshot.keyframes || {} });
          }
        );
        setRestoreError(null);
      } catch (error) {
        logger.error("[HistoryPanel] Error in undo:", error);
        setCanUndo(false);
        setRestoreError({ type: "undo", message: "Could not restore state" });
      }
    }
  };

  const handleRedo = () => {
    if (canRedo) {
      try {
        historyActions.redo(
          () => makeHistorySnapshot(getSceneState()),
          (snapshot) => {
            const robotActions = getSceneActions();
            robotActions.restoreState(snapshot.scene);
            useFileStore.setState({ keyframes: snapshot.keyframes || {} });
          }
        );
        setRestoreError(null);
      } catch (error) {
        logger.error("[HistoryPanel] Error in redo:", error);
        setCanRedo(false);
        setRestoreError({ type: "redo", message: "Could not restore state" });
      }
    }
  };

  const handleClearHistory = () => {
    try {
      historyActions.clearHistory();
      // Clear local state immediately for responsive UI
      setCanUndo(false);
      setCanRedo(false);
      setHistorySize(0);
      setUndoSummary("");
      setRedoSummary("");
      setHistoryList([]);
      setRestoreError(null);
    } catch (error) {
      logger.error("[HistoryPanel] Error clearing history:", error);
    }
  };

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="History Status"
      titleIcon={titleIcon}
      initialSize={{ width: 280, height: 320 }}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="p-3 space-y-3">
        <Row label="History Size">
          <span className="text-gray-200 text-sm">{historySize}</span>
        </Row>

        {/* History Actions */}
        <div className="px-3 py-2 space-y-3">
          {/* Undo Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-300 text-sm select-none">Undo</span>
              <button
                onClick={handleUndo}
                disabled={!canUndo}
                className={`px-3 py-1 text-xs rounded border ${
                  canUndo
                    ? "bg-blue-600 hover:bg-blue-700 text-white border-blue-500"
                    : "bg-gray-600 text-gray-400 cursor-not-allowed border-gray-500"
                }`}
              >
                Undo
              </button>
            </div>
            {restoreError?.type === "undo" && (
              <div className="text-xs text-rose-200 bg-rose-950/30 rounded px-2 py-1 border-l-2 border-rose-500">
                {restoreError.message}
              </div>
            )}
            {canUndo && undoSummary && (
              <div className="text-xs text-gray-300 bg-gray-800/50 rounded px-2 py-1 border-l-2 border-blue-500">
                {undoSummary}
              </div>
            )}
          </div>

          {/* Redo Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-300 text-sm select-none">Redo</span>
              <button
                onClick={handleRedo}
                disabled={!canRedo}
                className={`px-3 py-1 text-xs rounded border ${
                  canRedo
                    ? "bg-green-600 hover:bg-green-700 text-white border-green-500"
                    : "bg-gray-600 text-gray-400 cursor-not-allowed border-gray-500"
                }`}
              >
                Redo
              </button>
            </div>
            {restoreError?.type === "redo" && (
              <div className="text-xs text-rose-200 bg-rose-950/30 rounded px-2 py-1 border-l-2 border-rose-500">
                {restoreError.message}
              </div>
            )}
            {canRedo && redoSummary && (
              <div className="text-xs text-gray-300 bg-gray-800/50 rounded px-2 py-1 border-l-2 border-green-500">
                {redoSummary}
              </div>
            )}
          </div>
        </div>

        {/* History List */}
        {historyList.length > 0 && (
          <div className="px-3 pt-3 border-t border-gray-600">
            <div className="text-sm font-medium text-gray-300 mb-2">History</div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {historyList.map((item) => (
                <div
                  key={item.id}
                  className={`text-xs rounded px-2 py-1 flex items-center justify-between ${
                    item.disabled
                      ? "bg-rose-950/20 text-rose-200/70 border-l-2 border-rose-500/70 opacity-70"
                      : item.type === "past"
                        ? "bg-gray-800/50 text-gray-300 border-l-2 border-blue-500"
                        : "bg-gray-700/30 text-gray-300 border-l-2 border-green-500"
                  }`}
                >
                  <span className="flex-1 truncate">{item.summary}</span>
                  <span
                    className={`text-xs px-1 rounded ${
                      item.disabled
                        ? "bg-rose-900/50 text-rose-200"
                        : item.type === "past"
                          ? "bg-blue-600/50 text-blue-300"
                          : "bg-green-600/50 text-green-300"
                    }`}
                  >
                    {item.disabled ? "disabled" : item.type === "past" ? "✓" : "→"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="px-3 pt-2 border-t border-gray-600">
          <button
            onClick={handleClearHistory}
            className="w-full px-3 py-2 text-sm rounded border border-orange-500 bg-orange-600 hover:bg-orange-700 text-white transition-colors"
          >
            Clear History
          </button>
        </div>
      </div>
    </DraggablePanel>
  );
};

export default HistoryPanel;
